import { useState } from 'react'
import Link from 'next/link'

export function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="text-orange-600 w-4 h-4 flex-none mr-2 mt-1">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
    </svg>
  );
}


export default function Pricing() {

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  const frequencies = [
    { value: 'monthly', label: 'Monthly', priceSuffix: '/month' },
    { value: 'annually', label: 'Annually', priceSuffix: '/year' },
  ]

  const tiers = [
    {
      name: 'Basic',
      id: 'tier-basic',
      href: '#',
      price: { monthly: '$399' },
      description: 'The essentials to provide your best work for clients.',
      features: ['Catalog', 'Organizations and groups', 'Traditional login', 'Standard CKAN role based access control', 'Occasional security patches', 'Online Docs and tutorials', 'Datopian Cloud Hosting', 'Basic Support Plan'],
      mostPopular: false,
    },
    {
      name: 'Standard',
      id: 'tier-standard',
      href: '#',
      price: { monthly: '$899' },
      description: 'A plan that scales with your rapidly growing business.',
      features: [
        'GDPR compliancy',
        'Catalog',
        'Organizations and groups',
        'Traditional login',
        'Standard CKAN role based access control',
        'Regular security patches and updates',
        'Standard CKAN Harvesting',
        'Google Dataset Search, DCAT / interoperability',
        'Standard set of Data APIs from DataStore extension',
        'Rich integration with CKAN Pages',
        'Integration with ETL tools',
        'Workshop and Q&A session',
        'Custom Domain',
        'Datopian Cloud Hosting',
        'Basic Support Plan'
      ],
      mostPopular: false,
    },
    {
      name: 'Premium',
      id: 'tier-startup',
      href: '#',
      price: { monthly: '$1499' },
      description: 'Get the most from the Datopian professionals.',
      features: [
        'GDPR compliancy',
        'Catalog',
        'Organizations and groups',
        'Enhanced security: require complex passwords, limit login attempts',
        'Standard CKAN role based access control',
        'Brute force protection, Stronger password reset tokens, CSRF protection, Two Factor Authentication',
        'Data Integration: Bespoke importing workflows',
        'Aircan based on Airflow',
        'SEO optimization',
        'GraphQL engine for all your data',
        'Rich integration with WordPress',
        'Integration with ETL tools',
        'On-demand live Q&A sessions',
        'Custom Domain',
        'Datopian Cloud Hosting',
        'Silver Support Plan'
      ],
      mostPopular: false,
    },
  ]

  const enterprise = [
    {
      name: 'Enterprise',
      id: 'tier-enterprise',
      href: '#',
      price: { monthly: 'Contact us' },
      description: 'Dedicated support and infrastructure.',
      features: [
        'GDPR compliancy',
        'Catalog',
        'Organizations and groups',
        'SAML/OAuth, Azure Active Directory integration or similar',
        'Custom permissions based on your business logic',
        'Advanced Security: Pen-testing',
        'Data Integration: Quality checks, Custom permissions',
        'Any custom data processing pipelines',
        'SEO optimization',
        'Integrate your other data sources and expose via GraphQL',
        'Any CMS solution that provides an API to integrate',
        'Integration with ETL tools',
        'On-demand live Q&A sessions',
        'Custom Domain',
        'On-prem/Private cloud available for hosting',
        'Gold/Platinum Support Plan'
      ],
      mostPopular: false,
    },
  ]

  const support = [
    {
      name: 'Silver',
      id: 'tier-standard',
      href: '#',
      price: { monthly: '$500' },
      description: 'Essential Datopian support.',
      features: [
        'Email support',
        '24 hour response time',
        '24×7 (Online)',
        'Monitoring',
      ],
      mostPopular: false,
    },
    {
      name: 'Gold',
      id: 'tier-gold',
      href: '#',
      price: { monthly: '$1210' },
      description: 'Advanced support availability.',
      features: [
        'Email/Video call/Screen-share support',
        '8 hour response time',
        '24×7 (Online) 12×5 (Phone)',
        'Monitoring',
        'Dedicated issue board',
        'Dedicated account manager',
      ],
      mostPopular: false,
    },
    {
      name: 'Platinum',
      id: 'tier-platinum',
      href: '#',
      price: { monthly: '$2330' },
      description: 'Full dedicated support.',
      features: [
        'Email/Video call/Screen-share support',
        '4 hour response time',
        '24×7 (Online) 12×5 (Phone)',
        'Monitoring',
        'Dedicated issue board',
        'Dedicated account manager',
        'Custom SLA',
      ],
    }
  ]

  const basic = [
    {
      name: 'Basic',
      id: 'tier-basic',
      href: '#',
      price: { monthly: '$0' },
      description: 'Starting point for support.',
      features: [
        'Email support',
        '48 hour response time',
        '24×7 (Online)'
      ],
      mostPopular: false,
    },
  ]


  const [frequency, setFrequency] = useState(frequencies[0])

  const includedFeatures = [
    'Modern UI & Frontend design',
    'Catalog',
    'Organizations and groups',
    'Traditional login',
    'Standard CKAN role based access control',
    'Occasional security patches',
    'Online Docs and tutorials',
    'Datopian Cloud Hosting',
    'Basic Support Plan',
    'Add-ons Available',
  ]


  return (
    <div className="bg-white mt-16 sm:mt-24 !overflow-x-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 !font-['Inter']">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className='sr-only'>Datopian Pricing</h1>
          <p className="mt-2 text-4xl font-bold tracking-tight text-maingray sm:text-5xl">
            Flexible & Scalable Data Solutions
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-secgray">
          Check out our tailored CKAN Hosting & Support Plans
        </p>
        <div className="flex flex-col items-center justify-center">
          <div className="relative isolate overflow-hidden bg-maingray px-6 py-8 mt-6 text-center shadow-2xl rounded-3xl sm:px-16 w-full lg:max-w-4xl max-w-md md:max-w-2xl xl:max-w-none w-full">
            <p className="mx-auto max-w-3xl body-text !text-white ">
              Please note that the pricing on this page is specifically for hosting and support services. For pricing on our full range of services, which are customized based on your organization&apos;s needs, please <Link href="/contact"><span className="hover:cursor-pointer underline !hover:text-orange-400">contact</span></Link> our sales team for a personalized consultation.
            </p>
            <svg
              viewBox="0 0 1024 1024"
              className="absolute left-1/2 top-0 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
              aria-hidden="true"
            >
              <circle cx={512} cy={512} r={512} fill="url(#827591b1-ce8c-4110-b064-7cb85a0b1217)" fillOpacity="0.7" />
              <defs>
                <radialGradient id="827591b1-ce8c-4110-b064-7cb85a0b1217">
                  <stop stopColor="#fb923c" />
                  <stop offset={1} stopColor="#fb923c" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className="flex justify-center w-full">
          <div className="lg:max-w-4xl max-w-md md:max-w-2xl xl:max-w-none w-full">
            <h3 className="font-bold text-2xl text-maingray text-start w-full  mt-14">CKAN Hosting and Support</h3>
            <p className="body-text pt-4">Transform the way you manage and share data with our fully managed, cloud-based hosting solution for CKAN. Let us take care of the deployment, maintenance, and management of your CKAN instance so that you can focus on what matters most - your data and your users. </p>
          </div>
        </div>
        <div className="isolate mx-auto mt-12 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular ? 'ring-2 ring-orange-400' : 'ring-1 ring-gray-200 group hover:ring-orange-400',
                'rounded-3xl p-8'
              )}
            >
              <h3
                id={tier.id}
                className={classNames(
                  tier.mostPopular ? 'text-orange-400' : 'text-maingray group-hover:text-orange-400',
                  'text-lg font-semibold leading-8'
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 text-sm leading-6 text-secgray">{tier.description}</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <Link href="/contact"><span className="text-4xl font-bold tracking-tight text-maingray group-hover:text-orange-400 group-hover:cursor-pointer">{tier.price[frequency.value]}</span></Link>
                <span className="text-sm font-semibold leading-6 text-secgray">{frequency.priceSuffix}</span>
              </p>
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-secgray">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 mt-2  text-orange-400" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {enterprise.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular ? 'ring-2 ring-orange-400' : 'ring-1 ring-gray-200 group hover:ring-orange-400',
                'rounded-3xl p-8'
              )}
            >
              <h3
                id={tier.id}
                className={classNames(
                  tier.mostPopular ? 'text-orange-400' : 'text-maingray group-hover:text-orange-400',
                  'text-lg font-semibold leading-8'
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 text-sm leading-6 text-secgray">{tier.description}</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <Link href="/contact"><span className="text-4xl font-bold tracking-tight text-maingray group-hover:text-orange-400 group-hover:cursor-pointer">{tier.price[frequency.value]}</span></Link>
              </p>
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-secgray">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 mt-2 text-orange-400" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mx-auto max-w-7xl flex flex-col items-center justify-center xl:block">
          <div className="lg:max-w-4xl max-w-md md:max-w-2xl xl:max-w-none w-full">
            <h3 className="font-bold text-2xl text-maingray mt-14 text-start w-full  ">Special Offer: PortalJS Cloud</h3>
          </div>
          <div className="mt-14 group mx-auto  max-w-2xl rounded-3xl ring-1 ring-gray-200  lg:mx-0 lg:flex lg:max-w-4xl xl:max-w-none">
            <div className="p-8 sm:p-10 lg:flex-auto">
              <h3 className="text-2xl group-hover:text-orange-400 font-bold tracking-tight text-maingray">Empower your open data journey with our affordable solution for managing and showcasing open data</h3>
              <p className="mt-6 text-base leading-7 text-secgray">
                This exclusive offer delivers a customized data management solution that includes both the backend (<Link className="underline hover:text-orange-400 hover:cursor-pointer" href="/solutions/ckan">CKAN</Link> Instance) for storing and managing your data, and a frontend (<Link className="underline hover:cursor-pointer hover:text-orange-400" href="https://portaljs.com" target="_blank">PortalJS</Link>) that provides an attractive and easy-to-use interface for users to interact with the data.
              </p>
              <div className="mt-10 flex items-center gap-x-4">
                <h4 className="flex-none text-sm font-semibold leading-6 text-orange-400">What’s included</h4>
                <div className="h-px flex-auto bg-gray-100" />
              </div>
              <ul
                role="list"
                className="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-secgray sm:grid-cols-2 sm:grid-rows-5 sm:grid-flow-col "
              >
                {includedFeatures.map((feature, index) => (
                  <li
                    key={index}
                    className={`flex gap-x-3 ${index === includedFeatures.length - 1 ? 'font-bold' : ''}`}
                  >
                    <CheckIcon className="h-6 w-5 mt-2 text-indigo-600" aria-hidden="true" />
                    {feature}
                  </li>
                ))}

              </ul>
            </div>
            <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
              <div className="group relative isolate overflow-hidden bg-maingray  rounded-2xl py-10 text-center ring-1 ring-inset ring-maingray/5 lg:flex lg:flex-col lg:justify-center lg:py-16 h-full">
                <svg
                  viewBox="0 0 1024 1024"
                  className="absolute left-1/2 top-0 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
                  aria-hidden="true"
                >
                  <circle cx={512} cy={512} r={512} fill="url(#827591b1-ce8c-4110-b064-7cb85a0b1217)" fillOpacity="0.7" />
                  <defs>
                    <radialGradient id="827591b1-ce8c-4110-b064-7cb85a0b1217">
                      <stop stopColor="#fb923c" />
                      <stop offset={1} stopColor="#fb923c" />
                    </radialGradient>
                  </defs>
                </svg>
                <div className=" group mx-auto max-w-xs px-8">
                  <p className="mt-6 flex items-baseline justify-center gap-x-2">
                    <Link href="/contact"><span className="group-hover:text-orange-400 text-5xl font-bold tracking-tight group-hover:text-orange-400 !hover:cursor-pointer text-white">$99</span></Link>
                    <span className="text-sm font-semibold leading-6 tracking-wide text-white">/month</span>
                  </p>
                  <Link
                    href="/contact"
                    className="mt-10 block w-full btn-primary"
                  >
                    Contact us
                  </Link>
                  <Link
                    href="/solutions/open-data-portals"
                    className="mt-4 w-full inline-flex justify-center items-center py-3 px-7 text-base font-medium text-center text-maingray rounded-lg bg-white hover:bg-orange-600 hover:focus:ring-4 focus:ring-gray-100 shadow-sm no-underline caret-transparent hover:text-white"
                  >
                    Learn more
                  </Link>
                  <p className="mt-6 text-xs leading-5 text-white">
                    Invoices and receipts available for easy company reimbursement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center w-full mt-14">
          <div className="lg:max-w-4xl max-w-md md:max-w-2xl xl:max-w-none w-full">
            <h3 className="font-bold text-2xl text-maingray text-start w-full  ">CKAN Maintenance and Support</h3>
          </div>
        </div>
        <div className="isolate mx-auto mt-12 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
          {basic.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular ? 'ring-2 ring-orange-400' : 'ring-1 ring-gray-200 group hover:ring-orange-400',
                'rounded-3xl p-8'
              )}
            >
              <h3
                id={tier.id}
                className={classNames(
                  tier.mostPopular ? 'text-orange-400' : 'text-maingray group-hover:text-orange-400',
                  'text-lg font-semibold leading-8'
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 text-sm leading-6 text-secgray">{tier.description}</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <Link href="/contact"><span className="text-4xl font-bold tracking-tight text-maingray group-hover:text-orange-400 group-hover:cursor-pointer">{tier.price[frequency.value]}</span></Link>
                <span className="text-sm font-semibold leading-6 text-secgray">{frequency.priceSuffix}*</span>
              </p>
              <p className="text-xs text-orange-400 absolute">* Available only with a hosting plan</p>
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-secgray">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 mt-2  text-orange-400" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {support.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular ? 'ring-2 ring-orange-400' : 'ring-1 ring-gray-200 group hover:ring-orange-400',
                'rounded-3xl p-8'
              )}
            >
              <h3
                id={tier.id}
                className={classNames(
                  tier.mostPopular ? 'text-orange-400' : 'text-maingray group-hover:text-orange-400',
                  'text-lg font-semibold leading-8'
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 text-sm leading-6 text-secgray">{tier.description}</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <Link href="/contact"><span className="text-4xl font-bold tracking-tight text-maingray group-hover:text-orange-400 group-hover:cursor-pointer">{tier.price[frequency.value]}</span></Link>
                <span className="text-sm font-semibold leading-6 text-secgray">{frequency.priceSuffix}</span>
              </p>
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-secgray">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 mt-2  text-orange-400" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <section className='bg-white mt-24 mb-12 relative w-screen max-w-screen overflow-hidden'>
        <h3 className='text-center mx-auto medium-title font-["Inter"] text-white'>Need help choosing the right plan?</h3>
        <p className='text-center font-["Inter"] text-maingray mt-4 px-8 sm:px-0 mx-auto xl:max-w-4xl lg:max-w-3xl md:max-w-2xl max-w-sm'>Contact our sales team for expert advice and customized solutions tailored to your organization&apos;s needs.</p>
        <div className='flex flex-col  sm:flex-row mt-8 gap-y-8 sm:gap-x-8 w-full justify-center px-8 sm:px-0 mx-auto xl:max-w-4xl lg:max-w-3xl md:max-w-2xl max-w-sm'>
          <div className='btn-primary'><Link href="/contact">Contact Us</Link></div>
          <div className='btn-primary'><Link href="https://calendar.app.google/LT4acVdKn3Cxm2MXA" target="_blank">Schedule a free call</Link></div>
        </div>
      </section>
    </div>
  )
}
